import { EigApiError, useGetErrorResponse } from '@eigtech/ui-shared-api'
import { useGetAuth0UserName } from '@eigtech/ui-shared-api-requests'
import { Button } from '@eigtech/ui/components/button'
import { Skeleton } from '@eigtech/ui/components/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@eigtech/ui/components/tooltip'
import { cn } from '@eigtech/ui/lib/utils'
import { Info } from 'lucide-react'

export type Auth0UserNameProps = {
  user: string | undefined
  skeletonProps?: React.ComponentProps<'div'>
} & React.ComponentProps<'span'>

export function Auth0UserName({
  ref,
  className,
  user,
  skeletonProps,
  ...props
}: Auth0UserNameProps) {
  const { data, isError, isPending, error } = useGetAuth0UserName(user)

  const is404Error = error instanceof EigApiError && error.response.status === 404

  const text = (
    <span ref={ref} {...props}>
      {isPending ? 'Loading...' : isError ? user : data}
    </span>
  )

  const errorResponse = useGetErrorResponse(error)

  if (isPending) {
    return (
      <Skeleton
        render={<span />}
        {...skeletonProps}
        className={cn(className, skeletonProps?.className)}
      >
        {text}
      </Skeleton>
    )
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {text}
      {isError && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button className="bg-destructive/80" mode="icon" size="xs" variant="destructive">
                <Info />
              </Button>
            }
          />
          <TooltipContent>
            <span>{is404Error ? 'No user found with this ID' : errorResponse}</span>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
